<template>
  <ak-modal
    v-model:active="isModalActive"
    :submit-form="sendTestEmail"
    :outer-scroll="true"
    :flex-body="true"
    size="big"
  >
    <ak-modal-body>
      <ak-modal-title>Send Test Email</ak-modal-title>
      <p>Send a test email to 5 users at one time.</p>
      <ak-field label="Users">
        <ak-select
          v-model="to"
          placeholder="Select User"
          label="name"
          track-by="_id"
          :options="users"
          :multiple="true"
          :taggable="true"
          :max="max"
          :close-on-select="false"
        >
          <template #maxElements
            ><span class="text-danger"
              >Maximum of {{ max }} options selected.</span
            ></template
          >
        </ak-select>
      </ak-field>
    </ak-modal-body>

    <ak-modal-footer>
      <ak-modal-button type="submit" :disabled="!to.length"
        >Send Test</ak-modal-button
      >
    </ak-modal-footer>
  </ak-modal>
</template>

<script>
import { ref, watch, defineComponent } from 'vue'
import ModalButton from '@akerolabs/akero-vue/src/components/modal/ModalButton.vue'
import Modal from '@akerolabs/akero-vue/src/components/modal/Modal.vue'
import ModalTitle from '@akerolabs/akero-vue/src/components/modal/ModalTitle.vue'
import ModalBody from '@akerolabs/akero-vue/src/components/modal/ModalBody.vue'
import ModalFooter from '@akerolabs/akero-vue/src/components/modal/ModalFooter.vue'
import Field from '@akerolabs/akero-vue/src/components/field/Field.vue'
import Select from '@akerolabs/akero-vue/src/components/select/Select.vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-send-test-emails-modal></ak-send-test-emails-modal>
   */
  name: 'AkSendTestEmailsModal',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [ModalButton.name]: ModalButton,
    [Modal.name]: Modal,
    [ModalTitle.name]: ModalTitle,
    [Field.name]: Field,
    [Select.name]: Select,
    [ModalBody.name]: ModalBody,
    [ModalFooter.name]: ModalFooter,
  },

  /**
   * @type {Boolean}
   * @description This prevents all element attributes that are not
   * defined in the props object from being applied to the rendered
   * component root element, the exception to this is "class" all
   * of the given classes would be merged with the component
   * defined ones.
   */
  inheritAttrs: false,

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Boolean} Boolean flag to determine if the modal window is
     * active or not.
     */
    isActive: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Object}
     * @description The users profile data
     */
    userProfile: {
      default: () => ({}),
      type: Object,
    },

    /**
     * @type {Object}
     * @description List of active users on account
     */
    users: {
      default: () => [],
      type: Array,
    },

    /**
     * @type: {Function}
     * @description Function to handle sending the test emails
     */
    sendTest: {
      type: Function,
      default: () => {},
    },
  },

  emits: ['update:isActive'],

  /**
   * @type {Function}
   * @returns {Object}
   * @description Returns the default state of the component.
   */
  setup(props, { emit }) {
    const isModalActive = ref(props.isActive)
    const to = ref([])
    const max = ref(5)
    const currentUser = ref({
      _id: props.userProfile._id,
      name: props.userProfile.name,
      account: props.userProfile.account._id,
      agency: props.userProfile.agency._id,
      contactDetails: props.userProfile.contactDetails,
    })

    const sendTestEmail = () => {
      props.sendTest(to.value)
    }

    watch(isModalActive, (value) => {
      if (value === true) {
        to.value = []
        to.value.push(currentUser.value)
      }
      emit('update:isActive', value)
    })

    watch(
      () => props.isActive,
      (value) => {
        isModalActive.value = value
      },
    )

    return {
      isModalActive,
      to,
      max,
      currentUser,
      sendTestEmail,
    }
  },
})
</script>
